interface IDuplicatesConstraint {
  properties?: string[];
  message?: string;
  maximum?: number;
  caseSensitive?: boolean;
  /** Leave out items whose compared values are all empty (`undefined`, `null` or `''`). */
  ignoreEmpty?: boolean;
}

function identity(value: unknown): unknown {
  return value;
}

// Folds string values to lowercase; applied per compared value, not the whole
// row, so object rows never hit .toLowerCase() directly.
function foldCase(value: unknown): unknown {
  return typeof value === 'string' ? value.toLowerCase() : value;
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function duplicates(value: unknown, options: IDuplicatesConstraint = {}): string | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const validatorOptions = {
    properties: [] as string[],
    message: undefined as string | undefined,
    maximum: 0,
    caseSensitive: false,
    ignoreEmpty: false,
    ...options,
  };
  const normalize = validatorOptions.caseSensitive ? identity : foldCase;

  function isBlank(item: unknown): boolean {
    if (validatorOptions.properties.length) {
      return validatorOptions.properties.every((key) =>
        isEmpty((item as Record<string, unknown>)?.[key]),
      );
    }
    return isEmpty(item);
  }

  const target = validatorOptions.ignoreEmpty ? value.filter((item) => !isBlank(item)) : [...value];
  let count = 0;
  while (target.length > 0) {
    const item = target.shift();
    const duplicate = target.some((targetValue) => {
      if (validatorOptions.properties.length) {
        return validatorOptions.properties.every(
          (key) => normalize(item[key]) === normalize(targetValue[key]),
        );
      }
      return normalize(item) === normalize(targetValue);
    });
    if (duplicate) {
      count += 1;
    }
  }
  if (count > validatorOptions.maximum) {
    if (!validatorOptions.message) {
      validatorOptions.message = `must not have more than ${validatorOptions.maximum} duplicate values.`;
    }
    return validatorOptions.message;
  }
  return undefined;
}

export default duplicates;
export type { IDuplicatesConstraint };
